import { Loading } from '@/components/loading'
import { ensureConfigActivated } from '@/config/analytics'
import { useIsInMaintenance } from '@/config/maintenance'
import { useRemoteConfigListener } from '@/config/useRemoteConfigListener'
import { ReactNode, useEffect, useState } from 'react'
import { Maintenance } from './index'

const ACTIVATION_TIMEOUT_MS = 1500

/**
 * Top-level gate that decides whether to render the maintenance page or the
 * real app, based on the `IS_IN_MAINTENANCE` remote config flag.
 *
 * Blocks the initial render until Firebase remote config has activated (or a
 * short timeout elapses, in which case we fail open). The remote config
 * listener stays mounted here — not inside `<App>` — so we keep polling even
 * while the maintenance page is up, which is how we detect the flag flipping
 * back off.
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  useRemoteConfigListener()
  const isInMaintenance = useIsInMaintenance()
  const [activated, setActivated] = useState(false)

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      if (!cancelled) setActivated(true)
    }, ACTIVATION_TIMEOUT_MS)

    ensureConfigActivated().then(() => {
      if (cancelled) return
      clearTimeout(timer)
      setActivated(true)
    })

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [])

  if (!activated) return <Loading />
  if (isInMaintenance) return <Maintenance />
  return <>{children}</>
}
